import { OnwardLink, OnwardLinksUI } from "./OnwardLinksUI";
import { useEffect, useState } from "react";
import { ParsedUrlQuery } from "querystring";
import { useRouter } from "next/router";
import { StandardProps } from "@/components/componentMap.ts";
import { OnwardLinksESI } from "@/components/OnwardLinks/OnwardLinksESI.tsx";

export const ONWARD_LINK_CARD_CLICKED_EVENT = "ONWARD_LINK_CARD_CLICKED";
export const FOUND_IT_RESPONSE_KEY = "Product Page Links";
export const EXCLUDED_BRANDS = [
  "apple",
  "chanel",
  "dior",
  "dyson",
  "montblanc",
];

export enum OnwardLinkType {
  ESI_IMAGE_CARDS = "esi-image-cards",
  ESI_TEXT_LINKS = "esi-text-links",
  CLIENT_IMAGE_CARDS = "client-image-cards",
  CLIENT_TEXT_LINKS = "client-text-links",
}

export type FoundItResponse = {
  [FOUND_IT_RESPONSE_KEY]: Array<FoundItProductPageLink>;
};

export type FoundItProductPageLink = {
  AnchorText: string;
  DestinationUrl: string;
  ImageUrl: string;
  Order: number;
};

const FOUND_IT_API = "https://rtapi.foundit.com/page/5ec8b9c2/";
const MAX_RESULTS = 4;

function isOnwardLinkType(value = ""): value is OnwardLinkType {
  return Object.values<string>(OnwardLinkType).includes(value);
}

export function OnwardLinks({
  product,
  flags,
  configProps,
}: Pick<StandardProps, "flags" | "product" | "configProps">) {
  const queryParams: ParsedUrlQuery = useRouter().query;
  const [onwardsLinks, setOnwardsLinks] = useState<OnwardLink[]>([]);
  const [shouldRender, setShouldRender] = useState(true);
  const currentUrl = `https://www.johnlewis.com${useRouter().asPath.split("?")[0]}`;
  const isExcludedBrand =
    !product.brand?.name ||
    EXCLUDED_BRANDS.some(
      (item) => item.toLowerCase() === product.brand?.name.toLowerCase(),
    );
  const onwardLinkType =
    flags.onwardLinksType.value.toString() ||
    queryParams["onwardLinksType"]?.toString();
  const onwardLinkPosition = flags.onwardLinksPosition?.value;

  const isValidOnwardLinksType = isOnwardLinkType(onwardLinkType);

  const shouldShowOnwardLinks = isValidOnwardLinksType && !isExcludedBrand;

  const isOnwardLinkESI =
    isValidOnwardLinksType &&
    [OnwardLinkType.ESI_IMAGE_CARDS, OnwardLinkType.ESI_TEXT_LINKS].includes(
      onwardLinkType,
    );

  useEffect(() => {
    const fetchFoundItResults = async () => {
      try {
        const response = await fetch(
          `${FOUND_IT_API}${encodeURIComponent(encodeURIComponent(currentUrl))}`,
          { headers: { accept: "application/json" } },
        );

        if (response.ok) {
          const json = await response.json();

          if (json.Links) processFoundItResults(json.Links);
          else setShouldRender(false);
        } else {
          setShouldRender(false);
        }
      } catch {
        setShouldRender(false);
      }
    };

    if (shouldShowOnwardLinks && !isOnwardLinkESI) {
      fetchFoundItResults().then();
    }
  }, []);

  if (isOnwardLinkESI) {
    if (
      configProps?.onwardLinksPosition &&
      configProps.onwardLinksPosition != onwardLinkPosition &&
      configProps.onwardLinksPosition != undefined
    ) {
      return null;
    }

    return (
      <OnwardLinksESI onwardLinkType={onwardLinkType} currentUrl={currentUrl} />
    );
  }

  if (!shouldShowOnwardLinks) return null;

  const processFoundItResults = (resultJson: FoundItResponse) => {
    setOnwardsLinks(
      resultJson[FOUND_IT_RESPONSE_KEY].map((result) => {
        const urlObj = new URL(result["DestinationUrl"]);
        urlObj.searchParams.set("fi", "p");

        return {
          title: result["AnchorText"],
          url: urlObj.toString(),
          imageUrl: result["ImageUrl"],
        };
      }).slice(0, MAX_RESULTS),
    );

    setShouldRender(!!resultJson[FOUND_IT_RESPONSE_KEY].length);
  };

  return (
    shouldRender && (
      <OnwardLinksUI
        onwardLinkType={onwardLinkType}
        onwardLinks={
          onwardsLinks.length
            ? onwardsLinks
            : new Array(MAX_RESULTS).fill({ title: "", url: "" })
        }
      />
    )
  );
}
